import React from "react";
import {
  Info,
  AlertTriangle,
  Calendar,
  Zap,
  List,
  User,
  FileText,
  CheckCircle,
  Clock,
  Shield,
  AlertCircle,
  Mail,
  Phone,
  XCircle,
  Home,
  Layers,
  TrendingUp,
  Hash,
  Cpu,
  WifiIcon,
} from "lucide-react";
import { ReconnectionRequest } from "../../../types";
import { useParams } from "react-router-dom";
import { useMeterHistory } from "@features/cx/reconnect/hooks";

// Mocked types for services and SLA summary
export interface ServiceInfo {
  serviceType: string;
  meterNumber: string;
  deviceNumber: string;
  meterType: string;
  currentReading: string;
  readingDate: string;
}

export interface SlaSummary {
  slaTarget: {
    acknowledgementTime: string;
    responseTime: string;
    resolutionTime: string;
  };
  slaAchieved: {
    acknowledgementTime: string;
    responseTime: string;
    resolutionTime: string;
  };
}

interface ReconnectDetailsTabProps {
  request: ReconnectionRequest & {
    services: ServiceInfo[];
    slaSummary: SlaSummary;
  };
}

const labelClass = "text-xs text-gray-500";
const valueClass = "text-sm font-medium text-gray-900";
const sectionTitleClass =
  "font-semibold text-base flex items-center gap-2 mb-2";
const cardClass = "bg-white rounded-lg shadow border p-6";
const sectionClass = "mb-6";
const warningBoxClass =
  "bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded flex items-start gap-2";

const ReconnectDetailsTab: React.FC<ReconnectDetailsTabProps> = ({
  request,
}) => {
  const { action } = useParams();
  const { data: meterHistory } = useMeterHistory(request.consumer?.id, action);
  // Helper for NA
  const get = (val: any) =>
    val === undefined || val === null || val === "" ? "NA" : val;

  // Request Information
  const requestInfo = [
    {
      label: "Reason",
      value: get(request.requestType),
      icon: <FileText className="h-4 w-4" />,
    },
    {
      label: "Reason Code",
      value: get(request?.utilitySupportRequest?.configurationCode),
      icon: <AlertCircle className="h-4 w-4" />,
    },
    {
      label: "Description",
      value: get(request.utilitySupportRequest?.longDescription),
      icon: <AlertCircle className="h-4 w-4" />,
    },
  ];

  const requiredApproval =
    request?.utilitySupportRequest?.extraData?.acknowledgementTime ?? "-";

  // Notes, Instructions, Warnings
  const notes = get(request.consumerRemark);
  const accessInstructions = get(request.consumer?.serviceAddress);
  // For demo, safety warnings are NA
  const safetyWarnings = ["NA"];

  // SLA Section
  const defaultSla = {
    acknowledgementTime: "",
    responseTime: "",
    resolutionTime: "",
  };
  const slaTarget = request.slaSummary?.slaTarget || defaultSla;
  const slaAchieved = request.slaSummary?.slaAchieved || defaultSla;

  return (
    <div className="space-y-6">
      <div className={cardClass}>
        <div className="flex flex-col md:flex-row gap-8">
          {/* Request Information */}
          <div className="flex-1">
            <div className={sectionTitleClass}>
              <FileText className="h-5 w-5 text-gray-700" />
              <span className="card-heading-text">Request Information</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {requestInfo.map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div>
                    <div className={`card-subheading-text`}>{item.label}</div>
                    <div className={`text-sm`}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap gap-40 text-sm text-muted-foreground mt-8 p-4">
            <div>
              <p className="mb-1">Preferred Date</p>
              <p className="text-foreground font-medium">
                {request?.requestDate ?? "-"}
              </p>
            </div>
            <div>
              <p className="mb-1">Preferred Time</p>
              <p className="text-foreground font-medium">
                {request?.prefferedTimeSlotDisplay ?? "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
          {/* SLA Target */}
          <div className="border border-gray-200 p-4 rounded-md">
            <p className="card-heading-text mb-2">SLA Target</p>
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div className="text-center">
                <p className="card-subheading-text">Acknowledgement Time</p>
                <p className="text-foreground font-medium">
                  {request?.utilitySupportRequest?.extraData
                    ?.acknowledgementTime ?? "-"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Response Time</p>
                <p className="text-foreground font-medium">
                  {request?.utilitySupportRequest?.extraData?.responseTime ??
                    "-"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Resolution Time</p>
                <p className="text-foreground font-medium">
                  {request?.utilitySupportRequest?.extraData?.resolutionTime ??
                    "-"}
                </p>
              </div>
            </div>
          </div>

          {/* SLA Achieved */}
          <div className="border border-gray-200 p-4 rounded-md">
            <p className="card-heading-text mb-2">SLA Achieved</p>
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div className="text-center">
                <p className="card-subheading-text">Acknowledgement Time</p>
                <p className="text-green-600 font-medium">
                  {request?.acknowledgementTimeAchieved ?? "NA"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Response Time</p>
                <p className="text-green-600 font-medium">
                  {request?.acknowledgementTimeAchieved ?? "NA"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Resolution Time</p>
                <p className="text-green-600 font-medium">
                  {request?.resolutionTimeAchieved ?? "Pending"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Information */}
      {meterHistory?.length > 0 && action === "history" && (
        <div className="space-y-4">
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-gray-700" />
              <h2 className="card-heading-text">Allocation History</h2>
            </div>

            {meterHistory?.map((service, idx) => (
              <div key={idx} className="space-y-4 mt-2">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-medium text-gray-900">
                    {service.utilityService} Meter
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Number</div>
                      <div className={valueClass}>{service.meterNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Device Number</div>
                      <div className={valueClass}>{service.deviceNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Type</div>
                      <div className={valueClass}>{service.meterType}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <WifiIcon className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Utility Service</div>
                      <div className={valueClass}>{service.utilityService}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Current Reading</div>
                      <div className={valueClass}>{service.lastReading}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Reading Date</div>
                      <div className={valueClass}>
                        {service.lastReadingDate}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Status</div>
                      <div className={valueClass}>{service.meterStatus}</div>
                    </div>
                  </div>
                  {service.meterStatus === "Unassigned" && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Unassigned Date</div>
                        <div className={valueClass}>
                          {service.disconnectionDate}
                        </div>
                      </div>
                    </div>
                  )}
                  {service.meterStatus === "Assigned" && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Assigned Date</div>
                        <div className={valueClass}>
                          {service.allocationDate}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {meterHistory?.length > 0 && action === "current" && (
        <div className="space-y-4">
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-gray-700" />
              <h2 className="card-heading-text">Allocation History</h2>
            </div>

            {meterHistory?.map((service, idx) => (
              <div key={idx}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-medium text-gray-900">
                    {service.utilityService} Meter
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Number</div>
                      <div className={valueClass}>{service.meterNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Device Number</div>
                      <div className={valueClass}>{service.deviceNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Type</div>
                      <div className={valueClass}>{service.meterType}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <WifiIcon className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Utility Service</div>
                      <div className={valueClass}>{service.utilityService}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Current Reading</div>
                      <div className={valueClass}>{service.lastReading}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Reading Date</div>
                      <div className={valueClass}>
                        {service.lastReadingDate}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Status</div>
                      <div className={valueClass}>{service.meterStatus}</div>
                    </div>
                  </div>
                  {service.meterStatus === "Unassigned" && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Unassigned Date</div>
                        <div className={valueClass}>
                          {service.disconnectionDate}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Request Notes */}
      <div className={cardClass}>
        <div className={sectionTitleClass}>
          <List className="h-5 w-5 text-gray-700" />
          Request Notes
        </div>
        <div className="mt-2 text-sm text-gray-900">{notes}</div>
      </div>

      {/* Access Instructions */}
      {/* <div className={cardClass}>
        <div className={sectionTitleClass}>
          <FileText className="h-5 w-5 text-gray-700" />
          Description
        </div>
        <div className="mt-2 text-sm text-gray-900">{request?.utilitySupportRequest?.longDescription}</div>
      </div> */}
    </div>
  );
};

export default ReconnectDetailsTab;
